import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LoyaltyPointsSliderSearch from 'loyalty/components/LoyaltyPointsSliderSearch'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperienceSearchBurnCalculationKey } from 'lib/experiences/getExperienceSearchBurnCalculationKey'
import usePrefetchExperienceSearchBurnCalculations from '../hooks/usePrefetchExperienceSearchBurnCalculations'

const SliderContainer = styled.div`
  margin-bottom: ${rem(16)};
  min-width: 0;
`

interface Props {
  listIds?: Array<string>
}

function ExperienceSearchSocieteFilters({ listIds }: Props) {
  const intl = useIntl()
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  usePrefetchExperienceSearchBurnCalculations(listIds)
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()

  const viewPriceInPoints = !!searchFilters.viewPriceInPoints
  const experienceSearchKey = useMemo(
    () => getExperienceSearchBurnCalculationKey(searchFilters),
    [searchFilters],
  )

  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
    description: 'Checkbox label to show experience search prices as Société points only',
  })

  const viewPriceInPointsItems = useMemo<Array<FilterPanelCheckItem>>(() => [{
    value: 'view-price-in-points',
    checked: viewPriceInPoints,
    label: viewPriceInPointsLabel,
  }], [viewPriceInPoints, viewPriceInPointsLabel])

  const clearViewPriceInPoints = useCallback(() => {
    trackSearchFilter({
      label: viewPriceInPointsLabel,
      type: 'view_price_in_points',
      value: 'unselected',
    })
    setSearchFilters({ ...searchFilters, viewPriceInPoints: undefined })
  }, [
    searchFilters,
    setSearchFilters,
    trackSearchFilter,
    viewPriceInPointsLabel,
  ])

  const handleViewPriceInPointsToggle = useCallback(() => {
    if (viewPriceInPoints) {
      clearViewPriceInPoints()
    } else {
      trackSearchFilter({
        label: viewPriceInPointsLabel,
        type: 'view_price_in_points',
        value: 'selected',
      })
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      setSearchFilters({ ...searchFilters, viewPriceInPoints: true })
    }
  }, [
    viewPriceInPoints,
    clearViewPriceInPoints,
    searchFilters,
    setSearchFilters,
    setSearchBurnEnabled,
    setPointsToBurn,
    trackSearchFilter,
    viewPriceInPointsLabel,
  ])

  if (!isLoyaltyEnabled) {
    return null
  }

  return (
    <FilterPanelFilterGroup
      compact
      title={<LoyaltyTierLabel kind="primary">{getLoyaltyProgramDisplayName()}</LoyaltyTierLabel>}
    >
      <SliderContainer>
        <LoyaltyPointsSliderSearch
          layout="filterPanel"
          cacheKey={experienceSearchKey}
          skipBatchMaxCap
          skipBurnExperiment
          onBurnEnabled={clearViewPriceInPoints}
          disabled={viewPriceInPoints}
        />
      </SliderContainer>
      <FilterPanelCheckboxGroup
        name="viewPriceInPoints"
        items={viewPriceInPointsItems}
        onChange={handleViewPriceInPointsToggle}
      />
    </FilterPanelFilterGroup>
  )
}

export default ExperienceSearchSocieteFilters
